import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Seo } from '../components/Seo';
import { breadcrumb } from '../lib/structuredData';
import { api } from '../lib/api';
import { BadgeCheck, Search, Users, MapPin, Sparkles, ArrowRight, Compass } from 'lucide-react';

const SORTS = [['popular', 'Most popular'], ['followers', 'Most followers'], ['newest', 'Newest']];

const CreatorCard = ({ c, onOpen }) => (
  <Card className="rounded-2xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer group" onClick={() => onOpen(c.username)} data-testid={`creator-card-${c.username}`}>
    <div className="p-5">
      <div className="flex items-center gap-3">
        {c.profile_photo ? <img src={api.mediaUrl(c.profile_photo)} alt={c.name} className="h-14 w-14 rounded-2xl object-cover" /> : <span className="grid place-items-center h-14 w-14 rounded-2xl bg-accent text-accent-foreground text-lg font-semibold">{(c.name||'C').charAt(0)}</span>}
        <div className="min-w-0">
          <p className="font-semibold flex items-center gap-1.5 truncate">{c.name} {c.verification_status === 'verified' && <BadgeCheck className="h-4 w-4 text-accent shrink-0" />}</p>
          <p className="text-sm text-muted-foreground truncate">@{c.username}</p>
        </div>
      </div>
      {c.bio && <p className="text-sm text-muted-foreground mt-3 line-clamp-2">{c.bio}</p>}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {(c.creator_type||[]).slice(0, 2).map((t) => <Badge key={t} variant="outline" className="rounded-full text-[10px]">{t}</Badge>)}
        {(c.niches||[]).slice(0, 2).map((n) => <Badge key={n} className="rounded-full text-[10px] bg-accent/10 text-accent">{n}</Badge>)}
      </div>
      <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{(c.follower_count||0).toLocaleString()}</span>
        <span className="flex items-center gap-1"><Compass className="h-3.5 w-3.5" />{c.itinerary_count} itineraries</span>
        {(c.destinations||[])[0] && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{c.destinations[0]}</span>}
      </div>
      <Button variant="outline" className="w-full rounded-xl mt-4 group-hover:border-accent" data-testid={`view-creator-${c.username}`}>View Creator <ArrowRight className="h-4 w-4 ml-1" /></Button>
    </div>
  </Card>
);

export default function CreatorsMarketplace() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ creators: [], facets: {}, total: 0 });
  const [f, setF] = useState({ q: '', creator_type: '', platform: '', country: '', sort: 'popular', verified: false });

  useEffect(() => {}, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { sort: f.sort };
    if (f.q) params.q = f.q;
    if (f.creator_type) params.creator_type = f.creator_type;
    if (f.platform) params.platform = f.platform;
    if (f.country) params.country = f.country;
    if (f.verified) params.verified = '1';
    api.creatorsMarketplace(params).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const open = (u) => navigate(`/creators/${u}`);
  const facets = data.facets || {};
  const featured = (data.creators || []).filter((c) => c.verification_status === 'verified').slice(0, 3);

  return (
    <Layout>
      <Seo title="Travel Creators & Influencers"
        description="Discover verified travel creators and explore their hand-crafted itineraries and guides. Browse by platform, niche and destination on FlightDeals.in."
        path="/creators"
        jsonLd={breadcrumb([{ name: 'Home', path: '/' }, { name: 'Creators' }])} />
      <section className="relative">
        <div className="absolute inset-0 h-56"><img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1600&q=80" alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-b from-primary/85 to-background" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6 text-primary-foreground">
          <span className="inline-flex items-center gap-1.5 text-accent text-sm font-semibold"><Sparkles className="h-4 w-4" /> Creator Marketplace</span>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold">Discover top travel creators</h1>
          <p className="mt-2 text-primary-foreground/80 max-w-2xl">Browse verified creators and explore their hand-crafted itineraries.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="p-4 rounded-2xl mb-6">
          <div className="grid gap-3 md:grid-cols-5">
            <div className="relative md:col-span-2">
              <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
              <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search creators…" className="pl-9" data-testid="marketplace-search" />
            </div>
            <Select value={f.creator_type || 'all'} onValueChange={(v) => setF({ ...f, creator_type: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Creator type" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All types</SelectItem>{(facets.creator_types||[]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.platform || 'all'} onValueChange={(v) => setF({ ...f, platform: v === 'all' ? '' : v })}>
              <SelectTrigger data-testid="filter-platform"><SelectValue placeholder="Platform" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All platforms</SelectItem>{(facets.platforms||[]).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={f.sort} onValueChange={(v) => setF({ ...f, sort: v })}>
              <SelectTrigger data-testid="filter-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Switch checked={f.verified} onCheckedChange={(v) => setF({ ...f, verified: v })} data-testid="filter-verified" />
            <span className="text-sm text-muted-foreground flex items-center gap-1"><BadgeCheck className="h-4 w-4 text-accent" /> Verified only</span>
          </div>
        </Card>

        {/* Featured */}
        {!loading && featured.length > 0 && !f.q && !f.creator_type && (
          <div className="mb-8">
            <h2 className="font-display text-xl font-semibold mb-3">Featured creators</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{featured.map((c) => <CreatorCard key={c.creator_id} c={c} onOpen={open} />)}</div>
          </div>
        )}

        <h2 className="font-display text-xl font-semibold mb-3">{f.q || f.creator_type || f.platform ? 'Results' : 'All creators'} <span className="text-muted-foreground text-base font-normal">({data.total})</span></h2>
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}</div>
        ) : data.creators.length === 0 ? (
          <Card className="p-12 rounded-2xl text-center" data-testid="marketplace-empty"><Users className="h-10 w-10 mx-auto text-muted-foreground mb-2" /><p className="font-semibold">No creators found</p><p className="text-sm text-muted-foreground">Try adjusting your filters.</p></Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="marketplace-grid">{data.creators.map((c) => <CreatorCard key={c.creator_id} c={c} onOpen={open} />)}</div>
        )}
      </div>
    </Layout>
  );
}
